import React from 'react';

interface DeviceCodeDisplayProps {
  userCode: string;
  verificationUrl: string;
  verificationUrlComplete: string;
  polling: boolean;
  progressMessage: string;
}

export const DeviceCodeDisplay: React.FC<DeviceCodeDisplayProps> = ({
  userCode,
  verificationUrl,
  verificationUrlComplete,
  polling,
  progressMessage
}) => {
  // Create a single URL with the code as a query parameter
  const urlWithCode = `${verificationUrl}?code=${userCode}`;

  const copyUrlWithCode = async () => {
    try {
      await navigator.clipboard.writeText(urlWithCode);
      console.log('URL with code copied to clipboard');
    } catch (err) {
      console.error('Failed to copy URL:', err);
    }
  };

  const openBrowser = () => {
    window.open(verificationUrlComplete, '_blank', 'noopener,noreferrer');
  };

  return (
    <div className="device-code-container">
      <div className="auth-instructions">
        <h2>Complete authentication in your browser</h2>
        
        <div className="quick-option">
          <button onClick={openBrowser} className="quick-auth-button">
            🚀 Open Browser & Auto-fill Code
          </button>
          <p className="helper-text">Fastest option - opens browser with code pre-filled</p>
        </div>
        
        <div className="manual-option">
          <h3>Or copy the URL manually:</h3>
          
          <div className="step">
            <span className="step-number">1</span>
            <div className="step-content">
              <p>Copy this URL (includes your code):</p>
              <div className="url-display">
                <code>{urlWithCode}</code>
                <button onClick={copyUrlWithCode} className="copy-button">Copy URL</button>
              </div>
              <p className="url-note">This URL contains your authentication code: <strong>{userCode}</strong></p>
            </div>
          </div>
        </div>
        
        {polling && (
          <div className="polling-status">
            <div className="spinner"></div>
            <p>{progressMessage}</p>
            <p className="hint">This window will update automatically when you complete the login</p>
          </div>
        )}
      </div>
      
      <style>{`
        .device-code-container {
          max-width: 500px;
          margin: 0 auto;
          padding: 1rem;
          background: white;
          border-radius: 6px;
          box-shadow: 0 1px 6px rgba(0, 0, 0, 0.1);
        }

        .auth-instructions h2 {
          color: #333;
          margin-bottom: 1rem;
          text-align: center;
          font-size: 1.2rem;
        }

        .quick-option {
          margin-bottom: 1.25rem;
          text-align: center;
          padding: 0.75rem;
          border: 2px solid #0078d4;
          border-radius: 6px;
          background: #f0f8ff;
        }

        .quick-auth-button {
          background: linear-gradient(45deg, #0078d4, #106ebe);
          color: white;
          border: none;
          padding: 10px 20px;
          border-radius: 4px;
          font-size: 14px;
          font-weight: 600;
          cursor: pointer;
          width: 100%;
          transition: all 0.2s;
        }

        .quick-auth-button:hover {
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(0, 120, 212, 0.3);
        }

        .helper-text {
          margin: 0.25rem 0 0 0;
          color: #666;
          font-size: 12px;
        }

        .manual-option {
          border-top: 1px solid #eee;
          padding-top: 1rem;
        }

        .manual-option h3 {
          color: #666;
          font-size: 14px;
          margin-bottom: 0.75rem;
        }

        .step {
          display: flex;
          align-items: flex-start;
          margin-bottom: 1.5rem;
          gap: 1rem;
        }

        .step-number {
          background: #0078d4;
          color: white;
          width: 24px;
          height: 24px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: bold;
          font-size: 14px;
          flex-shrink: 0;
        }

        .step-content {
          flex: 1;
        }

        .step-content p {
          margin: 0 0 0.5rem 0;
          color: #333;
        }

        .url-display {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          background: #f8f9fa;
          padding: 0.75rem;
          border-radius: 4px;
          border: 1px solid #ddd;
        }

        .url-display code {
          flex: 1;
          font-family: 'Monaco', 'Consolas', monospace;
          font-size: 13px;
          color: #333;
          word-break: break-all;
          line-height: 1.4;
        }

        .url-note {
          margin-top: 0.5rem !important;
          font-size: 14px;
          color: #666;
        }

        .url-note strong {
          color: #0078d4;
          font-family: 'Monaco', 'Consolas', monospace;
          letter-spacing: 1px;
        }

        .copy-button {
          background: #6c757d;
          color: white;
          border: none;
          padding: 6px 12px;
          border-radius: 4px;
          font-size: 12px;
          cursor: pointer;
          white-space: nowrap;
        }

        .copy-button:hover {
          background: #5a6268;
        }

        .polling-status {
          margin-top: 2rem;
          text-align: center;
          padding: 1rem;
          background: #f8f9fa;
          border-radius: 8px;
        }

        .polling-status p {
          margin: 0.5rem 0;
          color: #333;
        }

        .hint {
          font-size: 14px;
          color: #666;
        }
      `}</style>
    </div>
  );
};